import React, { FC, PropsWithChildren, useEffect } from 'react';
import nextId from 'react-id-generator';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'antd';
import { SelectValue } from 'antd/es/select';
import moment from 'moment';
import { ProjectHttpService } from '@core/services/http';
import {
  arrsToSelectableObject,
  getFormData,
  isValidForm,
} from '@core/utils/methods';
import { serverValidator, uniqueValidator } from '@core/utils/validators';
import { ModalMainTypes } from '@models/enums';
import {
  AdminFrequencyDDV,
  AdminProjectCategoryData,
  OptionProps,
  ProjectCategory,
} from '@models/interfaces';
import { AddAndEditModal } from '@shared/components';
import { ProjectCategorySumModalProps } from '@shared/components/project-category/models';
import { Autocomplete, Checkbox, DatePicker, Input } from '@shared/modules';
import { ProjectCategoriesActions } from '@store/actions';
import {
  AdminProjectCategoriesSelectors,
  ProjectCategoriesSelectors,
} from '@store/selectors';

import './styles.scss';

const { useForm } = Form;

export const ProjectCategoryModal: FC<ProjectCategorySumModalProps> = ({
  itemValue,
  visible,
  modalType,
  toggleModal,
  projectCreate,
}: PropsWithChildren<ProjectCategorySumModalProps>) => {
  const [form] = useForm();
  const dispatch = useDispatch();

  const currentProjectCategories = useSelector(
    ProjectCategoriesSelectors.getProjectCategories,
  );
  const projectCategories = useSelector(
    AdminProjectCategoriesSelectors.getAdminProjectCategories,
  );
  const { dropdownsOptions, dropdownValues, currentProject } = useSelector(
    ProjectCategoriesSelectors.getProjectCategoryModalInfo,
  );

  const add = async (value: ProjectCategory): Promise<void> => {
    if (await isValidForm(form)) {
      dispatch(
        ProjectCategoriesActions.addProjectCategoriesItemAction.done({
          ...value,
          generatedId: nextId('PCCategory'),
        }),
      );
      toggleModal(modalType);
    }
  };

  const edit = async (value: ProjectCategory): Promise<void> => {
    if (await isValidForm(form)) {
      dispatch(
        ProjectCategoriesActions.updateProjectCategoriesItemAction.done(value),
      );
      toggleModal(modalType);
    }
  };

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        ...itemValue,
        projectAssignedDate: itemValue?.projectAssignedDate
          ? moment(itemValue?.projectAssignedDate)
          : null,
      });
    }
  }, [visible, itemValue]);

  const validationValues =
    modalType === ModalMainTypes.Edit
      ? currentProjectCategories.filter(
          (record: ProjectCategory) =>
            record.generatedId !== itemValue.generatedId,
        )
      : currentProjectCategories;

  const filteredProjectCategories = projectCategories.filter(
    (category: AdminProjectCategoryData) =>
      currentProjectCategories.find(
        (c: ProjectCategory) => c.projectCategory === category.id,
      )
        ? category.canBeDuplicated
        : category,
  );

  return (
    <AddAndEditModal
      onOk={
        modalType === ModalMainTypes.Add
          ? (): Promise<void> => add(getFormData(itemValue, form))
          : (): Promise<void> => edit(getFormData(itemValue, form))
      }
      onCancel={(): void => toggleModal(modalType)}
      visible={visible}
      type={modalType}
      formProps={{ labelCol: { span: 8 }, wrapperCol: { span: 15 }, form }}
      className="prov-project-category-modal"
      title="Project Category"
      cancelButtonProps={{ onClick: (): void => form.resetFields() }}
    >
      <Autocomplete
        id="projectCategory"
        label="Project Category"
        options={arrsToSelectableObject(
          filteredProjectCategories,
          'id',
          'value',
        )}
        formItemProps={{
          rules: [{ required: true, message: 'Project Category is required!' }],
        }}
      />
      <Checkbox id="isFirstNet" label="Is First Net?" />
      <Autocomplete
        id="projectRanking"
        label="Project Ranking"
        options={dropdownsOptions?.ProjectRanking}
      />
      <Input
        id="PACEProjectNumber"
        label="PACE Project Number"
        formItemProps={{
          rules: [
            { required: true, message: 'PACE Project Number is required!' },
            serverValidator(
              {
                method: 'getProjectProjectCategoryValidation',
                errorMessage: 'PACE Project Number must be unique',
              },
              ProjectHttpService,
              !projectCreate && modalType === ModalMainTypes.Edit
                ? currentProject.id
                : undefined,
              'PACEProjectNumber',
            ),
            uniqueValidator(
              validationValues,
              'PACEProjectNumber',
              'PACE Project Number',
            ),
          ],
        }}
      />
      <Input
        id="oracleProjectNumber"
        label="Oracle Project Number"
        formItemProps={{
          rules: [
            { required: true, message: 'Oracle Project Number is required!' },
            serverValidator(
              {
                method: 'getProjectProjectCategoryValidation',
                errorMessage: 'Oracle Project Number must be unique',
              },
              ProjectHttpService,
              !projectCreate && modalType === ModalMainTypes.Edit
                ? currentProject.id
                : undefined,
              'oracleProjectNumber',
            ),
            uniqueValidator(
              validationValues,
              'oracleProjectNumber',
              'Oracle Project Number',
            ),
          ],
        }}
      />
      <Autocomplete
        id="frequency"
        label="Project Frequency"
        options={dropdownsOptions?.Frequency.sort(
          (a: OptionProps, b: OptionProps) =>
            a.viewValue
              .toString()
              .localeCompare(b.viewValue.toString(), 'en', { numeric: true }),
        )}
        elementProps={{
          onChange: (id: SelectValue): void => {
            const frequency = dropdownValues.Frequency.find(
              (frequencyDDV: AdminFrequencyDDV) => frequencyDDV.id === id,
            );

            if (frequency) {
              form.setFieldsValue({ isFirstNet: frequency.isFirstNet });
            }
          },
        }}
      />
      <DatePicker id="projectAssignedDate" label="Project Assigned Date" />
      <Input id="strategicDriver" label="Strategic Driver" />
    </AddAndEditModal>
  );
};
